import { useMemo } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import type { ResultUseMatches } from '../../common/types/use-matches';
import { hasChildren, mergeSections } from '../../common/utils';
import { getActiveCommandList } from '../../common/utils/get-active-command-list';
import { getFallbackCommandResult } from '../../common/utils/get-fallback-command-result';
import { useCommandPalette } from '../command-palette';
import { useCommandPaletteSearchDebounced } from '../use-command-palette-search-debounced';
import type { UseMatchesArgs } from './types';
import { getFallback, useSortAndFilter } from './utils';

export const useMatches = ({ defaultFallback, rootFallback }: UseMatchesArgs): ResultUseMatches => {
	const [{ commands, expandedCommand, childResult, loadingList, sections, fallbackOverride }] =
		useCommandPalette();
	const [search] = useCommandPaletteSearchDebounced();
	const hasLoadChildren = hasChildren(expandedCommand);

	const selectedCommands = useMemo(
		() => (hasLoadChildren ? childResult?.commands || [] : commands),
		[hasLoadChildren, childResult?.commands, commands],
	);

	const selectedSections = useMemo(
		() => (hasLoadChildren ? childResult?.sections : mergeSections(sections)),
		[hasLoadChildren, childResult?.sections, sections],
	);

	const filterCommandsOverride = useMemo(
		() =>
			getActiveCommandList(expandedCommand, expandedCommand?.wasSecondaryTriggered)
				?.filterAndSortOverrideFn,
		[expandedCommand],
	);

	const flattenedResults = useSortAndFilter(
		search,
		selectedCommands,
		selectedSections,
		filterCommandsOverride,
	);
	if (ff('compal-issue-actions-very-high-priority_f5w5r')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const fallback = useMemo(
			() =>
				getFallback({
					rootFallback,
					defaultFallback,
					expandedCommand,
					noResults: Boolean(flattenedResults.results.length === 0 && !loadingList),
					fallbackOverride,
				}),
			[
				defaultFallback,
				expandedCommand,
				fallbackOverride,
				flattenedResults.results.length,
				loadingList,
				rootFallback,
			],
		);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMemo(() => {
			if (
				getActiveCommandList(expandedCommand, expandedCommand?.wasSecondaryTriggered)
					?.shouldShowNoCommandsWhileLoading &&
				loadingList
			) {
				return {
					results: [],
					total: 0,
				};
			}
			const fallbackCommandResult = getFallbackCommandResult(fallback);
			if (fallbackCommandResult) {
				return fallbackCommandResult;
			}

			return {
				...flattenedResults,
				fallback,
			};
		}, [expandedCommand, fallback, flattenedResults, loadingList]);
	}
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return useMemo(() => {
		if (
			getActiveCommandList(expandedCommand, expandedCommand?.wasSecondaryTriggered)
				?.shouldShowNoCommandsWhileLoading &&
			loadingList
		) {
			return {
				results: [],
				total: 0,
			};
		}

		const fallback = getFallback({
			rootFallback,
			defaultFallback,
			expandedCommand,
			noResults: Boolean(flattenedResults.results.length === 0 && !loadingList),
		});
		const fallbackCommandResult = getFallbackCommandResult(fallback);
		if (fallbackCommandResult) {
			return fallbackCommandResult;
		}

		return {
			...flattenedResults,
			fallback,
		};
	}, [defaultFallback, expandedCommand, flattenedResults, loadingList, rootFallback]);
};
